import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";

// CRM (Phase 2)
import CustomersList from "./pages/crm/CustomersList";
import CustomerDetail from "./pages/crm/CustomerDetail";
import PropertiesList from "./pages/crm/PropertiesList";
import PropertyDetail from "./pages/crm/PropertyDetail";
import SolarSystemsList from "./pages/crm/SolarSystemsList";
import SolarSystemDetail from "./pages/crm/SolarSystemDetail";
import EquipmentList from "./pages/crm/EquipmentList";
import EquipmentDetail from "./pages/crm/EquipmentDetail";
import WarrantiesList from "./pages/crm/WarrantiesList";
import WarrantyDetail from "./pages/crm/WarrantyDetail";

// Ops (Phase 3)
import ServiceRequestsList from "./pages/ops/ServiceRequestsList";
import ServiceRequestDetail from "./pages/ops/ServiceRequestDetail";
import WorkOrdersList from "./pages/ops/WorkOrdersList";
import WorkOrderDetail from "./pages/ops/WorkOrderDetail";
import DispatchBoard from "./pages/ops/DispatchBoard";
import MyWorkOrders from "./pages/ops/MyWorkOrders";
import DiagnosticReportPage from "./pages/ops/DiagnosticReportPage";

// Public (Phase 5b) — token-gated, no auth shell
import PublicReportView from "./pages/public/PublicReportView";

// Billing (Phase 6 / 7)
import PlansList from "./pages/billing/PlansList";
import PlanDetail from "./pages/billing/PlanDetail";
import SubscriptionsList from "./pages/billing/SubscriptionsList";
import SubscriptionDetail from "./pages/billing/SubscriptionDetail";
import CheckoutComplete from "./pages/billing/CheckoutComplete";

// Notifications (Phase 8)
import NotificationsList from "./pages/notifications/NotificationsList";
import NotificationDetail from "./pages/notifications/NotificationDetail";

// Diagnostic Reports list (Phase 5d)
import DiagnosticReportsList from "./pages/diagnostics/DiagnosticReportsList";

// RMA / Warranty (Phase RMA-4)
import RMARecordsList from "./pages/rma/RMARecordsList";
import RMADetail from "./pages/rma/RMADetail";

// Team (Phase 11)
import TeamList from "./pages/team/TeamList";
import TeamMemberDetail from "./pages/team/TeamMemberDetail";
import TeamMeRedirect from "./pages/team/TeamMeRedirect";

// Audit Log (Phase 12)
import AuditLogList from "./pages/audit/AuditLogList";

// Monitoring (Phase 14)
import MonitoringList from "./pages/monitoring/MonitoringList";
import MonitoringDetail from "./pages/monitoring/MonitoringDetail";
import MonitoringRules from "./pages/monitoring/MonitoringRules";

// Reports (Phase 13)
import ReportsPage from "./pages/reports/ReportsPage";

import "./App.css";

// CRM RBAC role gates — mirror backend.
const CUSTOMERS_READ = ["owner", "ops", "lead_tech"]; // tech_assistant blocked
const ALL_AUTH = ["owner", "ops", "lead_tech", "tech_assistant"];
// Ops (Phase 3) gates — mirror backend.
const OFFICE_ONLY = ["owner", "ops"]; // service requests + dispatch
const TECHS_ONLY = ["lead_tech", "tech_assistant"]; // /my-work

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Phase 5b — public report view (no auth, token-gated) */}
            <Route path="/public/reports/:token" element={<PublicReportView />} />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Customers — tech_assistant excluded */}
            <Route
              path="/customers"
              element={
                <ProtectedRoute roles={CUSTOMERS_READ}>
                  <CustomersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers/:id"
              element={
                <ProtectedRoute roles={CUSTOMERS_READ}>
                  <CustomerDetail />
                </ProtectedRoute>
              }
            />

            {/* Properties */}
            <Route
              path="/properties"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <PropertiesList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties/:id"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <PropertyDetail />
                </ProtectedRoute>
              }
            />

            {/* Solar Systems */}
            <Route
              path="/solar-systems"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <SolarSystemsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/solar-systems/:id"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <SolarSystemDetail />
                </ProtectedRoute>
              }
            />

            {/* Equipment */}
            <Route
              path="/equipment"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <EquipmentList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment/:id"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <EquipmentDetail />
                </ProtectedRoute>
              }
            />

            {/* Warranties */}
            <Route
              path="/warranties"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <WarrantiesList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/warranties/:id"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <WarrantyDetail />
                </ProtectedRoute>
              }
            />

            {/* Service Requests — office only */}
            <Route
              path="/service-requests"
              element={
                <ProtectedRoute roles={OFFICE_ONLY}>
                  <ServiceRequestsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/service-requests/:id"
              element={
                <ProtectedRoute roles={OFFICE_ONLY}>
                  <ServiceRequestDetail />
                </ProtectedRoute>
              }
            />

            {/* Work Orders — all roles (techs auto-scoped to assigned) */}
            <Route
              path="/work-orders"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <WorkOrdersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-orders/:id"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <WorkOrderDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-orders/:id/report"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <DiagnosticReportPage />
                </ProtectedRoute>
              }
            />

            {/* Dispatch — office only */}
            <Route
              path="/dispatch"
              element={
                <ProtectedRoute roles={OFFICE_ONLY}>
                  <DispatchBoard />
                </ProtectedRoute>
              }
            />

            {/* My Work — techs only */}
            <Route
              path="/my-work"
              element={
                <ProtectedRoute roles={TECHS_ONLY}>
                  <MyWorkOrders />
                </ProtectedRoute>
              }
            />

            {/* Billing — Phase 6 */}
            <Route
              path="/protection-plans"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <PlansList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protection-plans/:id"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <PlanDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <SubscriptionsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions/:id"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <SubscriptionDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing/checkout-complete"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <CheckoutComplete />
                </ProtectedRoute>
              }
            />

            {/* Notifications — Phase 8 (office only) */}
            <Route
              path="/notifications"
              element={
                <ProtectedRoute roles={OFFICE_ONLY}>
                  <NotificationsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications/:id"
              element={
                <ProtectedRoute roles={OFFICE_ONLY}>
                  <NotificationDetail />
                </ProtectedRoute>
              }
            />

            {/* Diagnostic Reports list — Phase 5d (office only) */}
            <Route
              path="/diagnostic-reports"
              element={
                <ProtectedRoute roles={OFFICE_ONLY}>
                  <DiagnosticReportsList />
                </ProtectedRoute>
              }
            />

            {/* RMA / Warranty — Phase RMA-4 (office only) */}
            <Route
              path="/rma-records"
              element={
                <ProtectedRoute roles={OFFICE_ONLY}>
                  <RMARecordsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rma-records/:rmaId"
              element={
                <ProtectedRoute roles={OFFICE_ONLY}>
                  <RMADetail />
                </ProtectedRoute>
              }
            />

            {/* Team — Phase 11 (all authenticated roles) */}
            <Route
              path="/team"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <TeamList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/me"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <TeamMeRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/:userId"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <TeamMemberDetail />
                </ProtectedRoute>
              }
            />

            {/* Audit Log — Phase 12 (owner only) */}
            <Route
              path="/audit-log"
              element={
                <ProtectedRoute roles={["owner"]}>
                  <AuditLogList />
                </ProtectedRoute>
              }
            />

            {/* Monitoring — Phase 14 (all roles for read; office for actions) */}
            <Route
              path="/monitoring"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <MonitoringList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monitoring/rules"
              element={
                <ProtectedRoute roles={["owner"]}>
                  <MonitoringRules />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monitoring/:alertId"
              element={
                <ProtectedRoute roles={ALL_AUTH}>
                  <MonitoringDetail />
                </ProtectedRoute>
              }
            />

            {/* Reports — Phase 13 (owner only) */}
            <Route
              path="/reports"
              element={
                <ProtectedRoute roles={["owner"]}>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
